<template>
  <teleport to="body">
    <transition-group name="toast" tag="div" class="toast-container">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="['toast', `toast-${toast.type}`]"
      >
        <div class="toast-icon">
          <span v-if="toast.type === 'success'">✓</span>
          <span v-else-if="toast.type === 'error'">✕</span>
          <span v-else-if="toast.type === 'warning'">⚠</span>
          <span v-else>ℹ</span>
        </div>
        <div class="toast-message">{{ toast.message }}</div>
      </div>
    </transition-group>
  </teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const toasts = ref([])
let toastIdCounter = 0

const showToast = (message, type = 'info', duration = 3000) => {
  const id = ++toastIdCounter
  const toast = {
    id,
    message,
    type,
    timer: null
  }

  toasts.value.push(toast)

  // 自动移除
  if (duration > 0) {
    toast.timer = setTimeout(() => {
      removeToast(id)
    }, duration)
  }

  return id
}

const removeToast = (id) => {
  const index = toasts.value.findIndex(t => t.id === id)
  if (index > -1) {
    const toast = toasts.value[index]
    if (toast.timer) {
      clearTimeout(toast.timer)
    }
    toasts.value.splice(index, 1)
  }
}

const clearAll = () => {
  toasts.value.forEach(toast => {
    if (toast.timer) {
      clearTimeout(toast.timer)
    }
  })
  toasts.value = []
}

// 暴露方法供外部调用
defineExpose({
  showToast,
  removeToast,
  clearAll
})

// 全局事件监听 - 注册到 window 供 useToast 使用
onMounted(() => {
  const toastInstance = {
    showToast,
    removeToast,
    clearAll
  }
  window.__toast = toastInstance
})

onUnmounted(() => {
  if (window.__toast) {
    delete window.__toast
  }
  clearAll()
})
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.toast {
  min-width: 280px;
  max-width: 90%;
  padding: 14px 20px;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
  animation: slideDown 0.3s ease-out;
}

.toast-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
}

.toast-message {
  flex: 1;
  color: #fff;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

/* 不同类型的样式 */
.toast-success {
  border-left: 3px solid #67c23a;
}

.toast-success .toast-icon {
  background: #67c23a;
  color: #fff;
}

.toast-error {
  border-left: 3px solid #f56c6c;
}

.toast-error .toast-icon {
  background: #f56c6c;
  color: #fff;
}

.toast-warning {
  border-left: 3px solid #e6a23c;
}

.toast-warning .toast-icon {
  background: #e6a23c;
  color: #fff;
}

.toast-info {
  border-left: 3px solid #409eff;
}

.toast-info .toast-icon {
  background: #409eff;
  color: #fff;
}

/* 动画 */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.toast-enter-active {
  animation: slideDown 0.3s ease-out;
}

.toast-leave-active {
  animation: slideUp 0.3s ease-in;
}

@keyframes slideUp {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20px);
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .toast-container {
    top: 10px;
    left: 10px;
    right: 10px;
    transform: none;
    width: auto;
  }

  .toast {
    min-width: auto;
    width: 100%;
  }
}
</style>

